import { Flex, Text } from '@chakra-ui/react';
import { Portal, createListCollection, Select } from '@chakra-ui/react';

type SphericalSelectProps = {
  value?: string[];
  onChange: (value: string[]) => void;
  options: { value: string }[];
};

export default function SphericalPowerSelect({
  value,
  onChange,
  options,
}: SphericalSelectProps) {
  const miopia = options
    .filter((opt) => parseFloat(opt.value.replace(' ', '')) < 0)
    .sort(
      (a, b) =>
        parseFloat(b.value.replace(' ', '')) -
        parseFloat(a.value.replace(' ', '')),
    );
  const hipermetropia = options
    .filter((opt) => parseFloat(opt.value.replace(' ', '')) >= 0)
    .sort(
      (a, b) =>
        parseFloat(a.value.replace(' ', '')) -
        parseFloat(b.value.replace(' ', '')),
    );

  const groupedOptions = [
    {
      label: 'Miopia',
      options: miopia.map((opt) => ({ label: opt.value, value: opt.value })),
    },
    {
      label: 'Hipermetropia',
      options: hipermetropia.map((opt) => ({
        label: opt.value.startsWith('+')
          ? opt.value
          : `${parseFloat(opt.value) > 0 ? '+' : ''}${opt.value}`,
        value: opt.value,
      })),
    },
  ];

  return (
    <Select.Root
      value={value ? value : undefined}
      onValueChange={(val) => {
        const selectedValue =
          Array.isArray(val) && val.length > 0
            ? (val[0]?.value ?? val[0])
            : typeof val === 'object' && val !== null && 'value' in val
              ? val.value
              : val;
        onChange(selectedValue);
      }}
      size="sm"
      w="100%"
      collection={createListCollection({
        items: groupedOptions.flatMap((group) =>
          group.options.map((opt) => ({ label: opt.label, value: opt.value })),
        ),
      })}
      positioning={{ placement: 'top', flip: false }}
    >
      <Select.HiddenSelect />
      <Select.Control>
        <Select.Trigger>
          <Select.ValueText placeholder="—" />
        </Select.Trigger>
        <Select.IndicatorGroup>
          <Select.Indicator />
        </Select.IndicatorGroup>
      </Select.Control>
      <Portal>
        <Select.Positioner>
          <Select.Content
            minW="220px"
            maxH="280px"
            overflowY="auto"
            bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
            p="0"
          >
            {/* Sticky column headers */}
            <Flex
              position="sticky"
              top="0"
              zIndex={10}
              bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
              borderBottom="1px solid"
              borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }}
            >
              <Flex
                flex="1"
                justify="center"
                py="7px"
                borderRight="1px solid"
                borderColor={{ base: 'rgba(0,0,0,0.06)', _dark: 'rgba(255,255,255,0.06)' }}
              >
                <Text
                  fontSize="10px"
                  fontWeight="700"
                  letterSpacing="0.5px"
                  textTransform="uppercase"
                  color={{ base: '#C53030', _dark: '#FC8181' }}
                >
                  Miopia
                </Text>
              </Flex>
              <Flex flex="1" justify="center" py="7px">
                <Text
                  fontSize="10px"
                  fontWeight="700"
                  letterSpacing="0.5px"
                  textTransform="uppercase"
                  color="#00B0CA"
                >
                  Hipermetropia
                </Text>
              </Flex>
            </Flex>

            {/* Two-column items */}
            <Flex direction="row" w="full">
              <Flex
                direction="column"
                flex="1"
                borderRight="1px solid"
                borderColor={{ base: 'rgba(0,0,0,0.05)', _dark: 'rgba(255,255,255,0.04)' }}
              >
                {groupedOptions[0].options.length === 0 ? (
                  <Flex align="center" justify="center" h="40px">
                    <Text fontSize="12px" color={{ base: 'rgba(0,0,0,0.3)', _dark: 'rgba(255,255,255,0.25)' }}>
                      —
                    </Text>
                  </Flex>
                ) : (
                  groupedOptions[0].options.map((opt, idx) => (
                    <Select.Item
                      item={{ label: opt.label, value: opt.value }}
                      key={`miopia-${idx}`}
                      color={{ base: '#C53030', _dark: '#FC8181' }}
                      _hover={{ bg: { base: 'rgba(197,48,48,0.06)', _dark: 'rgba(252,129,129,0.08)' } }}
                      justifyContent="center"
                      alignItems="center"
                      minH="32px"
                      p="0"
                      fontSize="13px"
                      fontWeight="500"
                    >
                      {opt.value}
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))
                )}
              </Flex>
              <Flex direction="column" flex="1">
                {groupedOptions[1].options.length === 0 ? (
                  <Flex align="center" justify="center" h="40px">
                    <Text fontSize="12px" color={{ base: 'rgba(0,0,0,0.3)', _dark: 'rgba(255,255,255,0.25)' }}>
                      —
                    </Text>
                  </Flex>
                ) : (
                  groupedOptions[1].options.map((opt, idx) => (
                    <Select.Item
                      item={{ label: opt.label, value: opt.value }}
                      key={`hiper-${idx}`}
                      color={{ base: '#006F7F', _dark: '#00D4F5' }}
                      _hover={{ bg: { base: 'rgba(0,176,202,0.07)', _dark: 'rgba(0,212,245,0.08)' } }}
                      alignItems="center"
                      justifyContent="center"
                      minH="32px"
                      p="0"
                      fontSize="13px"
                      fontWeight="500"
                    >
                      {opt.label}
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))
                )}
              </Flex>
            </Flex>
          </Select.Content>
        </Select.Positioner>
      </Portal>
    </Select.Root>
  );
}
